import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "../../base/skeleton/skeleton";
import { createLazyModuleLoader } from "../../../utils/lazy-module";
import { MermaidDiagramViewer } from "./mermaid-diagram-viewer";
/**
 * Renders a ```mermaid fence from an assistant turn (plan 010 §1).
 *
 * The source is model output steered by tenant-authored text, so it is treated
 * as untrusted throughout: a prompt-injected diagram must not be able to run
 * script, and a malformed one must not be able to break the answer around it.
 */ /**
 * Longer than this and the source is not a structural sketch any more. The cap
 * is checked BEFORE the dynamic import, so a pathological block never even pays
 * for the 1.5 MB download, let alone a layout pass. Mermaid's own `maxTextSize`
 * does not cover this — measured against 11.16.0, a 6 000-character label
 * rendered without complaint — so the guard has to live here.
 */ const MAX_SOURCE_LENGTH = 4000;
/**
 * Mermaid throws once a diagram exceeds this, which lands in the same catch as a
 * syntax error and therefore in the same code-block fallback. Layout is
 * superlinear in edge count, so the cap is what keeps a 500-edge graph from
 * freezing the transcript.
 */ const MAX_EDGES = 60;
/**
 * Mermaid merges a `%%{init: …}%%` directive found IN THE DIAGRAM SOURCE over
 * the config we pass, and its default `secure` list does not cover `htmlLabels`.
 * Verified against 11.16.0: `%%{init: {'htmlLabels': true}}%%` re-enabled HTML
 * labels and put a live `<img>` back into the SVG. These keys are the ones a
 * diagram must never be able to move, so they are frozen here.
 */ const LOCKED_CONFIG_KEYS = [
    "secure",
    "securityLevel",
    "startOnLoad",
    "maxTextSize",
    "maxEdges",
    "suppressErrorRendering",
    "htmlLabels",
    "flowchart",
    "class",
    // The theme keys are CSS injection: mermaid appends `themeCSS` verbatim into
    // the diagram's <style> block, and its directive sanitizer only strips values
    // containing angle brackets or `url(data:` — a position:fixed overlay with an
    // off-origin background-image passes untouched. DOMPurify keeps <style>, so
    // the second gate does not catch it either.
    "theme",
    "themeCSS",
    "themeVariables",
    "fontFamily",
    "altFontFamily"
];
/**
 * `securityLevel: 'strict'` sanitizes labels and drops `click` directives.
 * `htmlLabels: false` is the load-bearing one: with HTML labels on, mermaid
 * emits `<foreignObject>` wrapping arbitrary HTML, and DOMPurify deliberately
 * refuses to treat `foreignObject` as an HTML integration point (mXSS
 * hardening), so that subtree would be dropped wholesale and every node label
 * would vanish. With HTML labels off, mermaid draws labels as `<text>`/`<tspan>`
 * — which survive sanitization untouched — and markup inside a label becomes
 * visible literal text instead of an element.
 */ const MERMAID_CONFIG = {
    startOnLoad: false,
    securityLevel: "strict",
    htmlLabels: false,
    flowchart: {
        htmlLabels: false
    },
    class: {
        htmlLabels: false
    },
    maxEdges: MAX_EDGES,
    secure: LOCKED_CONFIG_KEYS
};
/**
 * The SVG profile keeps everything mermaid actually draws — `defs`, `marker`
 * (arrowheads), `viewBox` and the scoped `<style>` block all verified intact
 * against 11.16.0 — while removing script, event handlers and `foreignObject`.
 *
 * Anchors are forbidden on top of the profile: `securityLevel: 'strict'` only
 * blocks `click … call` CALLBACKS, while `click A href "https://…"` still
 * renders (sanitize-url passes https), and the SVG profile would keep
 * `<a href|xlink:href>` — handing a prompt-injected diagram a working phishing
 * link inside markup the zoom button invites the user to click. Nothing mermaid
 * draws for us needs an anchor or an href, so the element is unwrapped (its
 * label text survives) and the attributes are dropped everywhere, which also
 * keeps a hostile `<image href>` from fetching off-origin.
 */ const SANITIZE_CONFIG = {
    USE_PROFILES: {
        svg: true,
        svgFilters: true
    },
    FORBID_TAGS: [
        "a"
    ],
    FORBID_ATTR: [
        "href",
        "xlink:href"
    ]
};
/**
 * Loads mermaid on first use and initializes it exactly once.
 *
 * The import is dynamic so that mermaid (~1.5 MB) and DOMPurify land in their
 * own chunk rather than the chat page bundle — a user who never sees a diagram
 * never downloads one. A member-access gate would NOT achieve this; only a
 * genuinely dynamic import splits the chunk.
 */ const loadRenderer = createLazyModuleLoader(async ()=>{
    const [{ default: mermaid }, { default: DOMPurify }] = await Promise.all([
        import("mermaid"),
        import("dompurify")
    ]);
    mermaid.initialize(MERMAID_CONFIG);
    return async (id, source)=>{
        try {
            const { svg } = await mermaid.render(id, source);
            // Second, independent gate over the markup that reaches
            // dangerouslySetInnerHTML: mermaid's own sanitizer is configured by the
            // very source we distrust, so correctness here must not depend on it.
            return DOMPurify.sanitize(svg, SANITIZE_CONFIG);
        } finally{
            // A failed render leaves its measuring element behind (verified in
            // 11.16.0); without this every invalid diagram leaks a detached subtree.
            // `id` is unique per invocation (see `nextDiagramId`), so this can only
            // ever remove the element THIS call created — never a concurrent one's.
            document.getElementById(`d${id}`)?.remove();
        }
    };
});
let renderInvocations = 0;
/**
 * A fresh id for ONE call to `mermaid.render`, not one per component.
 *
 * Mermaid renders into `#<id>` inside a throwaway `#d<id>` container appended to
 * `<body>`, and reads that container back after an await to lift the SVG out of
 * it. Its own render queue (11.16.0) serializes the renders themselves — but the
 * cleanup above runs OUTSIDE that queue, in a `finally` the queue does not wait
 * for. So with an id shared between two draws, the first draw's cleanup lands in
 * the middle of the second's render, which then resumes to find its container
 * gone and throws `Cannot read properties of null (reading 'firstChild')`.
 *
 * That is not a corner case: StrictMode double-invokes the draw effect on every
 * mount, so a shared id meant NO diagram rendered in development at all — the
 * throw was caught, so it surfaced only as the code-block fallback. Two diagrams
 * in one turn, or a re-render mid-stream, collide the same way.
 *
 * The id must also be a valid CSS identifier: mermaid scopes the diagram's
 * `<style>` block with `#<id>`, so anything needing escaping (`useId`'s
 * colon-wrapped `:r3:`) would silently leak one diagram's styles into another's.
 */ function nextDiagramId() {
    renderInvocations += 1;
    return `mermaid-diagram-${renderInvocations}`;
}
export function MermaidDiagram({ source }) {
    const { t } = useTranslation();
    const [outcome, setOutcome] = useState(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const exceedsCap = source.length > MAX_SOURCE_LENGTH;
    const settled = outcome?.source === source ? outcome : null;
    // Rendering a diagram means driving an imperative third-party library that
    // measures real DOM — external synchronization, not derivable state.
    useEffect(function drawDiagram() {
        if (exceedsCap) {
            return;
        }
        // Per invocation, so overlapping draws never share mermaid's DOM scratch
        // space, and so the renderer's cleanup can only reach its own container.
        const id = nextDiagramId();
        let superseded = false;
        loadRenderer().then((render)=>render(id, source)).then((svg)=>{
            if (!superseded) {
                setOutcome({
                    source,
                    status: "drawn",
                    svg
                });
            }
        }).catch((error)=>{
            // Logged whether or not the outcome is still wanted: a silent catch
            // here is precisely what hid a StrictMode render race behind the
            // code-block fallback, in an app whose console looked clean.
            console.error("Mermaid diagram render failed:", error);
            if (!superseded) {
                setOutcome({
                    source,
                    status: "failed"
                });
            }
        });
        // A draw that is no longer the current one must not reach state — its
        // result belongs to a source or a mount that the transcript has moved on
        // from. It is left to finish so that it cleans up after itself.
        return ()=>{
            superseded = true;
        };
    }, [
        source,
        exceedsCap
    ]);
    if (exceedsCap || settled?.status === "failed") {
        return /*#__PURE__*/ _jsx(DiagramFallback, {
            source: source
        });
    }
    if (settled === null) {
        return /*#__PURE__*/ _jsx(MermaidDiagramSkeleton, {});
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-slot": "mermaid-diagram",
                "data-mermaid": true,
                className: "block w-full cursor-zoom-in overflow-x-auto rounded-md text-left hover:bg-accent/40",
                "aria-label": t("chat.diagram.expand"),
                onClick: ()=>setViewerOpen(true),
                children: /*#__PURE__*/ _jsx("div", {
                    role: "img",
                    "aria-label": t("chat.diagram.label"),
                    dangerouslySetInnerHTML: {
                        __html: settled.svg
                    }
                })
            }),
            /*#__PURE__*/ _jsx(MermaidDiagramViewer, {
                svg: settled.svg,
                open: viewerOpen,
                onOpenChange: setViewerOpen
            })
        ]
    });
}
/**
 * What a diagram degrades to when it cannot be drawn — too large, invalid
 * syntax, or mermaid itself failing to load. The source is still information, so
 * it is shown as an ordinary code block with a muted note rather than dropped;
 * an unrenderable diagram must never take the surrounding answer with it.
 */ function DiagramFallback({ source }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("pre", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsx("code", {
                    children: source
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mt-1 text-xs text-muted-foreground",
                children: t("chat.diagram.failed")
            })
        ]
    });
}
/**
 * Shown both while the fence is still streaming and while the mermaid chunk
 * downloads. Sized like a diagram card so the transcript does not jump when the
 * real drawing replaces it.
 */ export function MermaidDiagramSkeleton() {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs("div", {
        "data-mermaid": true,
        className: "flex h-40 w-full flex-col justify-center gap-3 rounded-md border p-4",
        role: "status",
        "aria-label": t("chat.diagram.loading"),
        children: [
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-4 w-1/3"
            }),
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-4 w-2/3"
            }),
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-4 w-1/2"
            })
        ]
    });
}

//# sourceMappingURL=mermaid-diagram.js.map